import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 2024</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>Important Disclaimer</h2>
            <p><strong>DATA RESPONSIBILITY:</strong> While Sahayak implements reasonable security measures, users acknowledge that no digital platform can guarantee 100% security. Users provide information at their own risk and Sahayak is not liable for any data breaches, unauthorized access, or misuse of information beyond our reasonable control.</p>
          </section>

          <section>
            <h2>1. Information We Collect</h2>
            <h3>Personal Information</h3>
            <ul>
              <li>Name, email address, and phone number for account registration</li>
              <li>Address and location data for service delivery</li>
              <li>Payment information for transaction processing</li>
              <li>Identity verification documents for helper registration</li>
              <li>Profile photos and service descriptions</li>
            </ul>

            <h3>Service Information</h3>
            <ul>
              <li>Service requests, bookings, and transaction history</li>
              <li>Reviews, ratings, and feedback</li>
              <li>Communication between customers and helpers</li>
              <li>Service completion records and quality assessments</li>
            </ul>

            <h3>Technical Information</h3>
            <ul>
              <li>Device information, IP address, and browser type</li>
              <li>Usage patterns and platform interactions</li>
              <li>Location data when using our mobile application</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li><strong>Service Delivery:</strong> Connect customers with qualified helpers and facilitate service bookings</li>
              <li><strong>Payment Processing:</strong> Process payments securely through our payment partners</li>
              <li><strong>Quality Assurance:</strong> Monitor service quality and resolve disputes</li>
              <li><strong>Safety & Security:</strong> Verify helper identities and maintain platform security</li>
              <li><strong>Communication:</strong> Send booking confirmations, updates, and important notifications</li>
              <li><strong>Platform Improvement:</strong> Analyze usage patterns to enhance user experience</li>
              <li><strong>Customer Support:</strong> Provide assistance and resolve issues</li>
              <li><strong>Legal Compliance:</strong> Meet regulatory requirements and legal obligations</li>
            </ul>
          </section>

          <section>
            <h2>3. Information Sharing</h2>
            <h3>With Service Providers</h3>
            <ul>
              <li>Helpers receive customer contact information and service location for service delivery</li>
              <li>Customers receive helper profiles, ratings, and contact information</li>
              <li>Payment processors receive necessary transaction information</li>
            </ul>

            <h3>Legal Requirements</h3>
            <ul>
              <li>We may disclose information to comply with legal obligations</li>
              <li>Law enforcement requests with valid legal authority</li>
              <li>Protection of rights, property, and safety of users</li>
            </ul>

            <h3>Business Transfers</h3>
            <p>In case of merger, acquisition, or sale of assets, user information may be transferred to the new entity.</p>
          </section>

          <section>
            <h2>4. Data Security</h2>
            <p><strong>SECURITY LIMITATIONS:</strong></p>
            <ul>
              <li>Industry-standard encryption for data transmission and storage</li>
              <li>Secure payment processing through certified payment gateways</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and employee training on data protection</li>
              <li>Incident response procedures for data breaches</li>
            </ul>
            <p><strong>DISCLAIMER:</strong> Despite our security measures, Sahayak cannot guarantee absolute security. Users acknowledge that internet transmission and electronic storage involves inherent security risks. Sahayak is not liable for unauthorized access, data breaches, or security incidents beyond our reasonable control.</p>
          </section>

          <section>
            <h2>5. Your Rights</h2>
            <ul>
              <li><strong>Access:</strong> Request copies of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Complaint:</strong> Report privacy concerns to relevant authorities</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <ul>
              <li>Account information retained while account is active</li>
              <li>Transaction records kept for 7 years for legal compliance</li>
              <li>Service history maintained for quality and dispute resolution</li>
              <li>Marketing data deleted upon opt-out request</li>
              <li>Deleted accounts purged within 30 days unless legally required</li>
            </ul>
          </section>

          <section>
            <h2>7. Cookies and Tracking</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Remember your preferences and login status</li>
              <li>Analyze platform usage and improve functionality</li>
              <li>Provide personalized service recommendations</li>
              <li>Ensure platform security and prevent fraud</li>
            </ul>
            <p>You can control cookie settings through your browser preferences.</p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>Sahayak is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If we become aware of such collection, we will delete the information immediately.</p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>Your information may be processed in countries outside your residence. We ensure appropriate safeguards are in place to protect your data according to this privacy policy. However, users acknowledge that different countries may have varying privacy laws and protections.</p>
          </section>

          <section>
            <h2>10. Privacy Limitations</h2>
            <p><strong>USER ACKNOWLEDGMENT:</strong></p>
            <ul>
              <li>Users understand that sharing personal information with helpers involves inherent privacy risks</li>
              <li>Sahayak is not responsible for helper's handling of customer information</li>
              <li>Users must exercise discretion when sharing sensitive personal information</li>
              <li>Sahayak cannot control how third parties (helpers, payment processors) handle shared data</li>
              <li>Users engage in information sharing entirely at their own risk</li>
            </ul>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>We may update this privacy policy periodically. Significant changes will be notified through email or platform notifications. Continued use of Sahayak constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>For privacy-related questions or requests, contact us at:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> sahayak.platform@gmail.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 